<template>
  <div class="card" @click="$emit('openContent')">
    <div
      :class="[
        'card__img-container',
        isSonarPlaylist && 'card__img-container--sonar',
      ]"
    >
      <div class="card__img-wrapper" :style="playlistColor">
        <div
          v-if="playlistDuration"
          class="card__duration-pill pill--processing"
        >
          {{ playlistDuration }}
        </div>
        <div class="card__header-bg" v-if="!isSonarPlaylist">
          <PlaylistIcon class="card__header-icon" />
          <PlaylistHeaderBg class="card__header-bg-vector" />
        </div>
        <img
          v-else
          class="card__img"
          src="../../assets/images/sonar-playlist.png"
          alt="Sonar Playlist"
        />
      </div>
    </div>
    <div class="card__content-wrapper">
      <div class="card__content-wrapper-main">
        <div class="card__content-wrapper-top-line">
          <h3 class="card__title" ref="playlistName">{{ playlist.name }}</h3>
        </div>
        <div class="card__content-wrapper-mid-line">
          <p
            class="card__description"
            :style="{
              '-webkit-line-clamp': descriptionMaxLines,
            }"
          >
            {{ playlist.description }}
          </p>
        </div>
        <div class="card__content-wrapper-bot-line">
          <PlaylistAssignedBy class="card__assigned-by" v-bind="{ playlist }" />
          <div class="card__footer-wrapper">
            <div class="card__line" />
            <div class="card__footer-cta-wrapper">
              <PlaylistProgress
                :userStatus="playlist.user_status"
                :userCompletion="playlist.user_completion"
                :hasTrackableContent="playlist.has_trackable_content"
              />
              <button class="card__cta-btn btn btn--secondary btn--small">
                {{ ctaLabel }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PlaylistIcon from '@/assets/icons/playlists/playlist_icon.svg'
import PlaylistProgress from './PlaylistProgress.vue'
import PlaylistAssignedBy from './PlaylistAssignedBy.vue'
import PlaylistHeaderBg from '@/assets/images/backgrounds/playlist-card-header-bg-vector.svg'

export default {
  name: 'PlaylistCard',
  inject: ['getBreakpoint'],
  components: {
    PlaylistIcon,
    PlaylistProgress,
    PlaylistAssignedBy,
    PlaylistHeaderBg: { ...PlaylistHeaderBg, name: 'PlaylistHeaderBg' },
  },
  props: {
    playlist: {
      type: Object,
      required: true,
    },
    isSonarPlaylist: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      titleHeight: 0,
    }
  },
  computed: {
    chevronIconSize() {
      const breakpoint = this.getBreakpoint()

      switch (breakpoint) {
        case 'mobile':
          return '16'

        case 'tablet':
          return '20'

        default:
          return '24'
      }
    },
    isMobile() {
      return this.getBreakpoint() === 'mobile'
    },
    playlistColor() {
      if (this.isSonarPlaylist)
        return 'background: var(--business-purple-glass)'
      const color = this.playlist.color

      return {
        '--playlist-bg-color': `${color}80`,
        '--playlist-ic-color': color,
      }
    },
    playlistDuration() {
      const { duration_in_seconds: duration } = this.playlist
      return this.$duration.durationFromSecs(duration)
    },
    ctaLabel() {
      const { progress } = this.playlist.user_status
      let action = 'Start'
      const label = 'playlist'
      if (progress === 0) return `${action} ${label}`
      action = progress === 1 ? 'Restart' : 'Resume'
      return `${action} ${label}`
    },
    descriptionMaxLines() {
      const MAX_TITLE_HEIGHT = 46
      return this.titleHeight >= MAX_TITLE_HEIGHT ? 2 : 3
    },
  },
  mounted() {
    this.titleHeight = this.$refs.playlistName.offsetHeight
  },
}
</script>

<style lang="scss" scoped>
.card {
  display: flex;
  border-radius: 10px;
  flex-direction: column;
  height: 465px;
  max-width: 400px;
  width: 100%;
  &:hover {
    cursor: pointer;
    .card__content-wrapper {
      background: linear-gradient(
        112.2deg,
        rgba(187, 188, 199, 0.4) -46.39%,
        rgba(187, 188, 199, 0) 112.38%
      );
    }
    .card__cta-btn {
      --btn-border-color: var(--btn-hover-border-color) !important;
    }
  }
  &:active {
    .card__content-wrapper {
      background: linear-gradient(
        111.02deg,
        rgba(18, 18, 18, 0.7) 3.86%,
        rgba(18, 18, 18, 0) 107.96%
      );
    }
    .card__cta-btn {
      --btn-bg-color: var(--btn-border-color) !important;
      --btn-text-color: var(--btn-secondary-active-text-color) !important;
    }
  }
  &__header {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 158px;
    &-bg-vector {
      width: 100%;
      position: absolute;
      left: 0;
      bottom: -20px;
      mix-blend-mode: overlay;
      filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25)) blur(32px);
      stroke-width: 1px;
      stroke: var(--purple-light);
    }
    &-icon {
      width: 117px;
      height: auto;
      rect {
        fill: transparent;
      }
      path {
        fill: var(--playlist-ic-color);
      }
    }
  }
  &__img-container {
    width: 100%;
    height: 158px;
    border-radius: 10px 10px 0px 0px;
    background: var(--spring-wood);
    align-self: stretch;
    &--sonar {
      background: none;
    }
  }
  &__img-wrapper {
    position: relative;
    border-radius: inherit;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: inherit;
    background-color: var(--playlist-bg-color);
    overflow: hidden;
    height: 158px;
  }
  &__img {
    width: 175px;
    height: 63px;
  }
  &__duration-pill {
    position: absolute;
    top: 10px;
    left: 10px;
    height: 24px;
    padding: 5px 8px;
    border-radius: 30px;
    color: var(--white);
    background: var(--dark-brighter);
    line-height: 14px;
    font-size: 12px;
  }

  &__content-wrapper {
    height: 307px;
    display: flex;
    flex: 1;
    padding: 10px;
    border-radius: 0px 0px 10px 10px;
    background: linear-gradient(
      104.99deg,
      rgba(62, 63, 73, 0.7) -13.91%,
      rgba(62, 63, 73, 0) 100.48%
    );
    padding: 35px 25px 35px 25px;
    border-radius: 0 0 10px 10px;
  }
  &__content-wrapper-main {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__content-wrapper-bot-line {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin-top: auto;
    font-size: rem(12px);
    line-height: 14px;
    color: var(--athens-gray);
    width: 100%;
    height: 100%;
    align-content: flex-end;
    gap: 0;
    @include tablet {
      column-gap: 30px;
    }
  }
  &__assigned-by {
    margin: 20px 0;
  }
  &__title {
    font-family: var(--font-medium);
    color: var(--white);
    font-size: rem(16px);
    line-height: 23px;
    margin: 0 0 10px 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    @include tablet {
      font-size: rem(18px);
      line-height: 24px;
    }
  }
  &__description {
    color: var(--light-blue-gray);
    height: 28px;
    font-size: rem(12px);
    line-height: px;
    margin: 0;
    height: auto;
    line-height: 20px;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    @include tablet {
      font-size: rem(14px);
      line-height: 20px;
    }
    @include desktop {
      font-size: rem(16px);
      line-height: 24px;
    }
  }
  &__line {
    width: 100%;
    height: 1px;
    background: var(--glass-extra-light);
  }
  &__info-wrapper {
    display: flex;
    gap: 30px;
    align-items: center;
  }
  &__footer-wrapper {
    width: 100%;
  }
  &__footer-cta-wrapper {
    margin-top: 20px;
    display: flex;
    gap: 10px;
    justify-content: space-between;
  }
}
</style>
